import { useMutation } from '@tanstack/react-query';
import { Share } from 'lucide-react';
import { overlay } from 'overlay-kit';
import { toast } from 'sonner';

import { downloadExportedPosts, getExportPostKey } from '@/entities/post/api/export-posts';
import { Button } from '@/shared/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { Skeleton } from '@/shared/components/shadcn/skeleton';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { Stage } from '@/shared/components/stage/stage';
import { cn } from '@/shared/utils/cn';
import { formatNumber } from '@/shared/utils/format-number';
import type { PostsParams, SortOption } from '../../../../entities/post/api/index';
import type { ReferencedPostsParams } from '../../referenced-posts/api';

type PostListType = 'posts' | 'referenced-posts';

type Props<T extends PostListType> = {
  type: T;
  className?: string;
  sortBy: SortOption;
  onSortChange: (sortBy: SortOption) => void;
  totalCount?: number;
  isSample: boolean;
  params: T extends 'posts' ? PostsParams : ReferencedPostsParams;
  isLoading: boolean;
  sortOptionList: { value: SortOption; label: string }[];
  hideTotalCount?: boolean;
  hideExport?: boolean;
  selectedCount?: number;
  onSaveSelected?: () => void;
  onClearSelected?: () => void;
};

export function SocialPostsGridHeader<T extends PostListType>({
  type,
  className,
  sortBy,
  onSortChange,
  totalCount,
  isLoading,
  sortOptionList,
  isSample,
  params,
  hideExport = false,
  hideTotalCount = false,
  selectedCount = 0,
  onSaveSelected,
  onClearSelected,
}: Props<T>) {
  return (
    <div className={cn('flex items-center justify-between gap-3 p-6 pb-0', className)}>
      <div className="flex flex-col gap-1.5">
        <Stage>
          <Stage.Scene status={isLoading}>
            <Skeleton className="h-6 w-24 rounded-md" />
          </Stage.Scene>
          <Stage.Scene default>
            <h2 className="text-xl leading-6 font-semibold text-zinc-950">
              Posts
              {!hideTotalCount && totalCount !== undefined && ` (${formatNumber(totalCount)})`}
            </h2>
          </Stage.Scene>
        </Stage>
      </div>

      <div className="flex items-center gap-3">
        {selectedCount > 0 && (
          <>
            <Button variant="default" size="sm" onClick={onSaveSelected}>
              Save Selected ({selectedCount})
            </Button>
            <Button variant="outline" size="sm" onClick={onClearSelected}>
              Clear
            </Button>
            <div className="h-6 w-px bg-zinc-200" />
          </>
        )}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-zinc-500">sort by</span>
          <Select value={sortBy} onValueChange={onSortChange}>
            <SelectTrigger className="h-9 w-auto gap-2 border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-900">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sortOptionList.map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!hideExport && <div className="h-6 w-px bg-zinc-200" />}
        {!hideExport && (
          <TooltipFlat
            asChild
            side="bottom"
            content={
              isSample ? (
                <p>
                  Sample queries cannot be exported. Upgrade your plan to create your own queries
                  and access full post data.
                </p>
              ) : (
                <p>Export to CSV</p>
              )
            }
          >
            <div className="inline-block">
              <Button
                variant="outline"
                size="icon"
                className="size-9"
                disabled={isSample}
                onClick={() => {
                  if (isSample) return;
                  if (type === 'referenced-posts') return;
                  const postParams = params as PostsParams;
                  overlay.open(({ isOpen, close }) => (
                    <ExportPostsModal isOpen={isOpen} onClose={close} params={postParams} />
                  ));
                }}
              >
                <Share />
              </Button>
            </div>
          </TooltipFlat>
        )}
      </div>
    </div>
  );
}

const ExportPostsModal = ({
  isOpen,
  onClose,
  params,
}: {
  isOpen: boolean;
  onClose: () => void;
  params: PostsParams;
}) => {
  const exportPostsMutation = useMutation({
    mutationFn: getExportPostKey,
    onSuccess: (data: { key: string }) => {
      downloadExportedPosts(data.key);
      onClose();
    },
    onError: () => {
      toast.error('Failed to export posts');
    },
  });

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent hidePrimitiveClose className="gap-0 p-0">
        <div className="flex flex-col gap-6 p-6">
          <DialogHeader>
            <DialogTitle>Export Posts</DialogTitle>
          </DialogHeader>
          <ul className="list-outside list-disc space-y-2 pl-6 text-sm text-zinc-500">
            <li>
              The export will include post content, author profiles, engagement metrics, video
              analysis, and demographic insights.
            </li>
            <li>The download may take about 15 seconds.</li>
          </ul>
          <DialogFooter>
            <Button onClick={onClose} variant="outline" disabled={exportPostsMutation.isPending}>
              Cancel
            </Button>
            <Button
              disabled={exportPostsMutation.isPending}
              onClick={() => exportPostsMutation.mutate(params)}
            >
              <Share />
              Export
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
